import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Dialog, Transition } from "@headlessui/react";

// Assume these icons are imported from an icon library
import {
    BoxCubeIcon,
    DollarLineIcon,
    ChevronDownIcon,
    EnvelopeIcon,
    GridIcon,
    HorizontaLDots,
    ListIcon,
    LockIcon,
    PageIcon,
    PieChartIcon,
    PlugInIcon,
    SettingsIcon,
    ShootingStarIcon,
} from "../icons";
import UploadIcon from "../icons/UploadIcon";
import DownloadIcon from "../icons/DownloadIcon";
import SearchIcon from "../icons/SearchIcon";
import DocumentIcon from "../icons/DocumentIcon";
import BuildingIcon from "../icons/BuildingIcon";
import MapIcon from "../icons/MapIcon";
import ShieldIcon from "../icons/ShieldIcon";
import { useSidebar } from "../context/useSidebar";
import { useAuth } from "../contexts/useAuth";
import { isAdmin, isSuperAdmin, isExclusive } from "../utils/authUtils";
import { getProfileImageUrl, businessApi } from "../config/api";
import { isMarketplaceEnabled, isOpportunityFeedEnabled } from "../config/features";

type NavItem = {
    name: string;
    icon: React.ReactNode;
    path?: string;
    subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

/**
 * The DA Intelligence navigation from spec screen 1d. This is the product the marketplace pivot
 * sells, so it sits at the top with no group heading, exactly as the mockup shows.
 *
 * My listing has no screen yet — it is rendered as an ordinary link so the intended shape is
 * visible, and will 404 until that page is built.
 */
const intelligenceItems: NavItem[] = [
    {
        icon: <GridIcon />,
        // Named Dashboard, not Matches: this IS the dashboard now. The old outreach dashboard keeps
        // the same label under Old Tabs — two entries with one name, distinguished by their section,
        // which is what makes the swap legible while both still exist.
        name: "Dashboard",
        // Was "/", which is the public marketplace now.
        path: "/dashboard",
    },
    {
        icon: <ShootingStarIcon />,
        name: "Saved",
        path: "/saved",
    },
    {
        icon: <EnvelopeIcon />,
        name: "Alert settings",
        path: "/alert-settings",
    },
    {
        icon: <MapIcon />,
        name: "Development Map",
        path: "/development-map",
    },
    {
        icon: <BuildingIcon />,
        name: "My listing",
        path: "/my-listing",
    },
];

const navItems: NavItem[] = [
    {
        icon: <GridIcon />,
        // The old outreach dashboard. "/" now belongs to the new one, so this points at the route
        // the old page moved to rather than silently opening the new one.
        name: "Dashboard",
        path: "/old-dashboard",
    },
    {
        icon: <BuildingIcon />,
        name: "Business Settings",
        path: "/business-settings",
    },
    {
        icon: <DollarLineIcon />,
        name: "Billing",
        path: "/billing",
    },
    {
        icon: <SearchIcon />,
        name: "Search",
        path: "/search",
    },
    {
        icon: <PieChartIcon />,
        name: "Analytics",
        path: "/analytics",
    },
];

// In-development features, gated off production and grouped in their own sidebar section rather
// than mixed into the day-to-day Menu items.
const featuresItems: NavItem[] = [
    {
        icon: <BoxCubeIcon />,
        name: "Marketplace",
        path: "/",
    },
    {
        icon: <ListIcon />,
        name: "Opportunity feed",
        // `path` is what the feature filter below keys off. It does NOT navigate: renderMenuItems
        // branches on subItems before path, so an item carrying both renders as a submenu toggle —
        // which is why the feed screen itself is listed as the first sub-item rather than relying on
        // the parent. That is how every other submenu in this file reaches its own landing screen.
        path: "/opportunities",
        subItems: [
            { name: "Opportunity feed", path: "/opportunities" },
            { name: "My opportunities", path: "/opportunities/board" },
            { name: "Matching rules", path: "/opportunities/matching-rules" },
            { name: "Smart Card", path: "/opportunities/smart-card" },
        ],
    },
    // Marketplace is still in development — hide it on production (staging + local dev only).
].filter((item) =>
    (item.path !== "/" || isMarketplaceEnabled()) &&
    (item.path !== "/opportunities" || isOpportunityFeedEnabled()));

const reportsNavItem: NavItem = {
    icon: <DocumentIcon />,
    name: "Reports",
    path: "/reports",
};

const managementItems: NavItem[] = [
    {
        icon: <ListIcon />,
        name: "Subscriptions",
        path: "/admin/subscriptions",
    },
    {
        icon: <PageIcon />,
        name: "Contact Requests",
        path: "/admin/contact-requests",
    },
    {
        icon: <DocumentIcon />,
        name: "Exclusive Dispatches",
        path: "/admin/exclusive-dispatches",
    },
    {
        icon: <EnvelopeIcon />,
        name: "Send my Business",
        path: "/admin/info-packs",
    },
    {
        icon: <ListIcon />,
        name: "Sales Pipeline",
        path: "/admin/deals",
    },
    {
        icon: <LockIcon />,
        name: "Security",
        path: "/admin/security",
    },
];

const adminItems: NavItem[] = [
    {
        icon: <SettingsIcon />,
        name: "Background Services",
        path: "/admin/background-services",
    },
    {
        icon: <SettingsIcon />,
        name: "Email Preview",
        path: "/admin/email-preview",
    },
    {
        icon: <SearchIcon />,
        name: "Data Extraction",
        path: "/data-extraction",
    },
    {
        icon: <UploadIcon />,
        name: "File Uploader",
        path: "/file-uploader",
    },
    {
        icon: <DownloadIcon />,
        name: "File Downloader",
        path: "/file-downloader",
    },
    {
        icon: <DocumentIcon />,
        name: "PDF Uploader",
        path: "/pdf-uploader",
    },
    {
        icon: <SearchIcon />,
        name: "Business Details",
        path: "/google-places",
    },
];

const AppSidebar: React.FC = () => {
    const { isExpanded, isMobileOpen, isHovered, setIsHovered, toggleMobileSidebar } = useSidebar();
    const { user, logout, activeBusiness } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const userInitials = useMemo(() => {
        if (!user?.firstName || !user?.lastName) return "";
        return (user.firstName[0] + user.lastName[0]).toUpperCase();
    }, [user?.firstName, user?.lastName]);

    const [isSupportOpen, setIsSupportOpen] = useState(false);
    const [sidebarImageError, setSidebarImageError] = useState(false);
    /** The pre-pivot client navigation, parked at the bottom while the new DA Intelligence nav leads. */
    const [oldTabsOpen, setOldTabsOpen] = useState(false);
    /** Drill-down: false shows the client nav, true swaps the same sidebar to the internal tooling. */
    const [adminView, setAdminView] = useState(false);
    const [emailIntegrationEnabled, setEmailIntegrationEnabled] = useState(false);

    useEffect(() => {
        businessApi.getMyBusiness()
            .then((b) => setEmailIntegrationEnabled(b.emailIntegrationEnabled ?? false))
            .catch(() => { /* silently ignore — feature stays hidden */ });
    }, []);

    const [openSubmenu, setOpenSubmenu] = useState<{
        type: "main" | "management" | "features";
        index: number;
    } | null>(null);
    const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
        {}
    );
    const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

    // const isActive = (path: string) => location.pathname === path;
    const isActive = useCallback(
        (path: string) => location.pathname === path,
        [location.pathname]
    );

    useEffect(() => {
        let submenuMatched = false;
        const sections: { type: "main" | "features"; items: NavItem[] }[] = [
            { type: "main", items: navItems },
            { type: "features", items: featuresItems },
        ];
        sections.forEach(({ type, items }) => {
            items.forEach((nav, index) => {
                if (nav.subItems) {
                    nav.subItems.forEach((subItem) => {
                        if (isActive(subItem.path)) {
                            setOpenSubmenu({ type, index });
                            submenuMatched = true;
                        }
                    });
                }
            });
        });

        if (!submenuMatched) {
            setOpenSubmenu(null);
        }
    }, [location, isActive]);

    useEffect(() => {
        if (openSubmenu !== null) {
            const key = `${openSubmenu.type}-${openSubmenu.index}`;
            if (subMenuRefs.current[key]) {
                setSubMenuHeight((prevHeights) => ({
                    ...prevHeights,
                    [key]: subMenuRefs.current[key]?.scrollHeight || 0,
                }));
            }
        }
    }, [openSubmenu]);

    const handleSubmenuToggle = (index: number, menuType: "main" | "management" | "features") => {
        setOpenSubmenu((prevOpenSubmenu) => {
            if (
                prevOpenSubmenu &&
                prevOpenSubmenu.type === menuType &&
                prevOpenSubmenu.index === index
            ) {
                return null;
            }
            return { type: menuType, index };
        });
    };

    const renderMenuItems = (items: NavItem[], menuType: "main" | "management" | "features") => (
        <ul className="flex flex-col gap-4">
            {items.map((nav, index) => (
                <li key={nav.name}>
                    {nav.subItems ? (
                        <button
                            onClick={() => handleSubmenuToggle(index, menuType)}
                            className={`menu-item group ${openSubmenu?.type === menuType && openSubmenu?.index === index
                                ? "menu-item-active"
                                : "menu-item-inactive"
                                } cursor-pointer ${!isExpanded && !isHovered
                                    ? "lg:justify-center"
                                    : "lg:justify-start"
                                }`}
                        >
                            <span
                                className={`menu-item-icon-size  ${openSubmenu?.type === menuType && openSubmenu?.index === index
                                    ? "menu-item-icon-active"
                                    : "menu-item-icon-inactive"
                                    }`}
                            >
                                {nav.icon}
                            </span>
                            {(isExpanded || isHovered || isMobileOpen) && (
                                <span className="menu-item-text">{nav.name}</span>
                            )}
                            {(isExpanded || isHovered || isMobileOpen) && (
                                <ChevronDownIcon
                                    className={`ml-auto w-5 h-5 transition-transform duration-200 ${openSubmenu?.type === menuType &&
                                        openSubmenu?.index === index
                                        ? "rotate-180 text-brand-500"
                                        : ""
                                        }`}
                                />
                            )}
                        </button>
                    ) : (
                        nav.path && (
                            <Link
                                to={nav.path}
                                onClick={() => isMobileOpen && toggleMobileSidebar()}
                                className={`menu-item group ${isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                                    }`}
                            >
                                <span
                                    className={`menu-item-icon-size ${isActive(nav.path)
                                        ? "menu-item-icon-active"
                                        : "menu-item-icon-inactive"
                                        }`}
                                >
                                    {nav.icon}
                                </span>
                                {(isExpanded || isHovered || isMobileOpen) && (
                                    <span className="menu-item-text">{nav.name}</span>
                                )}
                            </Link>
                        )
                    )}
                    {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
                        <div
                            ref={(el) => {
                                subMenuRefs.current[`${menuType}-${index}`] = el;
                            }}
                            className="overflow-hidden transition-all duration-300"
                            style={{
                                height:
                                    openSubmenu?.type === menuType && openSubmenu?.index === index
                                        ? `${subMenuHeight[`${menuType}-${index}`]}px`
                                        : "0px",
                            }}
                        >
                            <ul className="mt-2 space-y-1 ml-9">
                                {nav.subItems.map((subItem) => (
                                    <li key={subItem.name}>
                                        <Link
                                            to={subItem.path}
                                            onClick={() => isMobileOpen && toggleMobileSidebar()}
                                            className={`menu-dropdown-item ${isActive(subItem.path)
                                                ? "menu-dropdown-item-active"
                                                : "menu-dropdown-item-inactive"
                                                }`}
                                        >
                                            {subItem.name}
                                            <span className="flex items-center gap-1 ml-auto">
                                                {subItem.new && (
                                                    <span
                                                        className={`ml-auto ${isActive(subItem.path)
                                                            ? "menu-dropdown-badge-active"
                                                            : "menu-dropdown-badge-inactive"
                                                            } menu-dropdown-badge`}
                                                    >
                                                        new
                                                    </span>
                                                )}
                                                {subItem.pro && (
                                                    <span
                                                        className={`ml-auto ${isActive(subItem.path)
                                                            ? "menu-dropdown-badge-active"
                                                            : "menu-dropdown-badge-inactive"
                                                            } menu-dropdown-badge`}
                                                    >
                                                        pro
                                                    </span>
                                                )}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );

    return (
        <aside
            /* Solid dark green in BOTH themes, per spec screen 1d — the sidebar is the product's
               identity here, not a surface that follows the light/dark toggle. */
            className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-brand-700 dark:bg-brand-900 border-brand-800 dark:border-brand-950 text-white h-[calc(100vh-4rem)] lg:h-screen transition-all duration-300 ease-in-out z-50 border-r
        ${isExpanded || isMobileOpen
                    ? "w-[290px]"
                    : isHovered
                        ? "w-[290px]"
                        : "w-[90px]"
                }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
            onMouseEnter={() => !isExpanded && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className="py-8 hidden lg:flex justify-center w-full"
            >
                <Link to="/dashboard" className="flex items-center justify-center w-full">
                    {/* The "_dark_logo" files are the LIGHT-INK marks meant for dark backgrounds.
                        The sidebar is green in both themes now, so they are used unconditionally —
                        the dark-ink pair would be invisible against it. */}
                    {isExpanded || isHovered || isMobileOpen ? (
                        <>
                            <img
                                className="mx-auto"
                                src="/images/logo/DA-Connect_logo_title_transparent_dark_logo.svg"
                                alt="Logo"
                                width={150}
                                height={40}
                            />
                        </>
                    ) : (
                        <>
                            <img
                                className="mx-auto"
                                src="/images/logo/DA-Connect_logo_dark_logo.svg"
                                alt="Logo"
                                width={32}
                                height={32}
                            />
                        </>
                    )}
                </Link>
            </div>
            {/* min-h-0 is load-bearing. This is a flex-1 child of a `flex flex-col` aside, and flex
                items default to min-height:auto — they refuse to shrink below their content. Without
                it the container grows past the aside instead of staying at its height, so
                overflow-y-auto never engages and the overflowing nav simply spills out of the
                viewport with no way to reach it. */}
            <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar flex-1 min-h-0 pt-6 lg:pt-0">
                <nav className="mb-6">
                    {/* Two views of one sidebar. The client nav is what a subscriber sees; the
                        internal groups live behind the Admin link pinned at the bottom, so the
                        day-to-day nav is not buried under tooling. Drilling in swaps the contents
                        rather than routing, so it works from any page and the admin screens keep
                        their existing URLs. */}
                    {adminView ? (
                    <div className="flex flex-col gap-4" data-testid="admin-nav">
                        <button
                            onClick={() => setAdminView(false)}
                            className="flex items-center gap-2 mb-2 text-xs uppercase leading-[20px] text-brand-200 hover:text-white cursor-pointer"
                        >
                            <span aria-hidden>←</span>
                            <span>Back to app</span>
                        </button>
                        {isAdmin(user) && (
                        <div className="">
                            <h2
                                className={`mb-4 text-xs uppercase flex leading-[20px] text-brand-200 ${!isExpanded && !isHovered
                                    ? "lg:justify-center"
                                    : "justify-start"
                                    }`}
                            >
                                {isExpanded || isHovered || isMobileOpen ? (
                                    "management"
                                ) : (
                                    <HorizontaLDots />
                                )}
                            </h2>
                            {renderMenuItems(managementItems, "management")}
                        </div>
                        )}
                        {isSuperAdmin(user) && (
                            <div className="">
                                <h2
                                    className={`mb-4 text-xs uppercase flex leading-[20px] text-brand-200 ${!isExpanded && !isHovered
                                        ? "lg:justify-center"
                                        : "justify-start"
                                        }`}
                                >
                                    {isExpanded || isHovered || isMobileOpen ? (
                                        "admin"
                                    ) : (
                                        <HorizontaLDots />
                                    )}
                                </h2>
                                {renderMenuItems(adminItems, "main")}
                            </div>
                        )}

                    </div>
                    ) : (
                    <div className="flex flex-col gap-4">
                        {/* DA Intelligence leads, with no group heading — it is the product now,
                            not a section of the old app. */}
                        <div data-testid="intelligence-nav">
                            {renderMenuItems(intelligenceItems, "main")}
                        </div>

                        {/* ── Old Tabs ───────────────────────────────────────────────
                            The pre-pivot client navigation, parked at the bottom and collapsed by
                            default while the DA Intelligence nav leads. Kept rather than deleted
                            so the old screens stay reachable during the transition. Admin,
                            management and controls are NOT in here — they moved behind the Admin
                            link, being internal tooling rather than client tabs. */}
                        {(isExpanded || isHovered || isMobileOpen) && (
                            <div className="mt-auto pt-4" data-testid="old-tabs">
                                <button
                                    onClick={() => setOldTabsOpen(prev => !prev)}
                                    aria-expanded={oldTabsOpen}
                                    className="flex items-center w-full mb-2 text-xs uppercase leading-[20px] text-brand-200 hover:text-white cursor-pointer"
                                >
                                    <span>Old Tabs</span>
                                    <ChevronDownIcon
                                        className={`ml-auto w-4 h-4 transition-transform duration-200 ${oldTabsOpen ? "rotate-180" : ""}`}
                                    />
                                </button>
                                {oldTabsOpen && (
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            {renderMenuItems([
                                                ...(isExclusive(activeBusiness) ? [...navItems, reportsNavItem] : navItems),
                                                ...(emailIntegrationEnabled ? [{ icon: <PlugInIcon />, name: "Integrations", path: "/integrations" }] : []),
                                                ...(emailIntegrationEnabled ? [{ icon: <PageIcon />, name: "Marketing", path: "/marketing" }] : []),
                                            ], "main")}
                                        </div>
                                        {featuresItems.length > 0 && (
                                            <div>
                                                <h2 className="mb-4 text-xs uppercase flex leading-[20px] text-brand-200 justify-start">
                                                    features
                                                </h2>
                                                {renderMenuItems(featuresItems, "features")}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    )}
                </nav>

            </div>

            {/* Its own labelled section at the foot of the sidebar, using the same menu-item
                styling as every other nav entry so it reads as a destination rather than an
                afterthought. Hidden entirely from non-admins. */}
            {!adminView && isAdmin(user) && (isExpanded || isHovered || isMobileOpen) && (
                <div className="mb-4 flex-shrink-0 border-t border-white/10 pt-4">
                    <h2 className="mb-2 text-xs uppercase leading-[20px] text-brand-200">Admin</h2>
                    <button
                        data-testid="admin-link"
                        onClick={() => setAdminView(true)}
                        className="menu-item group menu-item-inactive w-full cursor-pointer"
                    >
                        <span className="menu-item-icon-size menu-item-icon-inactive">
                            <ShieldIcon />
                        </span>
                        <span className="menu-item-text">Admin Panel</span>
                    </button>
                </div>
            )}

            {/* Plan card, as in the mockup. Deliberately a SIBLING of the scroll container rather
                than inside it, so it stays pinned to the bottom of the sidebar instead of scrolling
                away with the nav. flex-shrink-0 keeps the nav taking the squeeze, not this.

                The mockup's second line reads "6 councils · unlimited alerts", but the auth context
                carries no council list and this is not worth an extra request for a sidebar caption
                — so it shows the subscription state, which is true, rather than a guessed count. */}
            {(isExpanded || isHovered || isMobileOpen) && activeBusiness && (
                <div
                    data-testid="plan-card"
                    className="mb-6 flex-shrink-0 rounded-xl bg-white/10 px-4 py-3"
                >
                    <div className="text-[11px] font-bold uppercase tracking-wide text-brand-200">
                        {activeBusiness.planName || 'No active plan'}
                    </div>
                    {activeBusiness.subscriptionStatus && (
                        <div className="mt-1 text-xs capitalize text-brand-100">
                            {activeBusiness.subscriptionStatus}
                        </div>
                    )}
                </div>
            )}

            {/* Mobile user section */}
            {isMobileOpen && (
                <div className="border-t border-white/15 pt-4 pb-6 px-5 lg:hidden">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="h-10 w-10 min-w-[2.5rem] rounded-full bg-blue-600 flex items-center justify-center text-white font-medium text-sm overflow-hidden">
                            {user?.profilePictureUrl && !sidebarImageError ? (
                                <img src={getProfileImageUrl(user.id)} alt="" className="h-full w-full object-cover" onError={() => setSidebarImageError(true)} />
                            ) : (
                                userInitials
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-white truncate">
                                {user ? `${user.firstName} ${user.lastName}` : 'User'}
                            </div>
                            <div className="text-xs text-brand-200 truncate">
                                {user?.email}
                            </div>
                        </div>
                    </div>
                    <Link
                        to="/profile"
                        onClick={() => toggleMobileSidebar()}
                        className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-brand-100 rounded-lg hover:bg-white/10 hover:text-white"
                    >
                        <svg className="h-5 w-5 text-brand-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Account Settings
                    </Link>
                    <button
                        onClick={() => setIsSupportOpen(true)}
                        className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-brand-100 rounded-lg hover:bg-white/10 hover:text-white w-full"
                    >
                        <svg className="h-5 w-5 text-brand-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                        </svg>
                        Support
                    </button>
                    <button
                        onClick={async () => {
                            toggleMobileSidebar();
                            await logout();
                            navigate('/login');
                        }}
                        className="flex items-center gap-3 px-3 py-2 text-sm font-medium text-brand-100 rounded-lg hover:bg-white/10 hover:text-white w-full"
                    >
                        <svg className="h-5 w-5 text-brand-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                        </svg>
                        Sign Out
                    </button>
                </div>
            )}

            {/* Support Modal */}
            <Transition appear show={isSupportOpen} as={Fragment}>
                <Dialog as="div" className="relative z-[100]" onClose={() => setIsSupportOpen(false)}>
                    <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <div className="fixed inset-0 bg-black/10 dark:bg-black/40" />
                    </Transition.Child>
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all border-t-4 border-brand-500">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-brand-500 dark:text-brand-300">
                                        Contact Support
                                    </Dialog.Title>
                                    <div className="mt-4">
                                        <p className="text-sm text-gray-500 dark:text-gray-300">Our support team is here to help you.</p>
                                        <div className="mt-4 space-y-2">
                                            <div>
                                                <p className="font-medium text-brand-600 dark:text-brand-200">Business Hours:</p>
                                                <p className="text-gray-600 dark:text-gray-300">Monday - Friday: 9:00 AM - 5:00 PM AEST</p>
                                            </div>
                                            <div>
                                                <p className="font-medium text-brand-600 dark:text-brand-200">Email:</p>
                                                <a href="mailto:council@daconnect.com.au" className="text-brand-500 hover:text-brand-600 hover:underline dark:text-brand-300 dark:hover:text-brand-200">
                                                    council@daconnect.com.au
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-6">
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-transparent bg-brand-100 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:bg-brand-700 dark:text-brand-100 dark:hover:bg-brand-600"
                                            onClick={() => setIsSupportOpen(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </aside>
    );
};

export default AppSidebar;
